import { DatePipe } from '@angular/common';
import { Shift } from '../interfaces';
import { ProjectWithShifts } from '../components/calendar/calendar.component';
import { DateHelper } from '../helpers';

export class ResourceUtil {
	private static todayCache: Date | null = null;

	public static isToday(date: Date): boolean {
		if (!ResourceUtil.todayCache || ResourceUtil.isNewDay()) {
			ResourceUtil.todayCache = new Date();
		}

		return date.getDate() === ResourceUtil.todayCache.getDate() &&
			date.getMonth() === ResourceUtil.todayCache.getMonth() &&
			date.getFullYear() === ResourceUtil.todayCache.getFullYear();
	}

	// Helper method to check if the cached date is from a previous day
	private static isNewDay(): boolean {
		const now = new Date();
		return ResourceUtil.todayCache!.getDate() !== now.getDate() ||
			ResourceUtil.todayCache!.getMonth() !== now.getMonth() ||
			ResourceUtil.todayCache!.getFullYear() !== now.getFullYear();
	}

	public static formatDate(date: Date): string {
		const d = new Date(date);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}

	public static getStartOfWeek(date: Date, firstDayOfTheWeek: number = 1): Date {
		const copy = new Date(date);
		// Calculate how many days to go back to reach the firstDayOfTheWeek
		const currentDayOfWeek = copy.getDay();
		let daysToSubtract = (currentDayOfWeek - firstDayOfTheWeek + 7) % 7;
		// Go back to the start of the week
		copy.setDate(copy.getDate() - daysToSubtract);
		return copy;
	}

	public static getEndOfWeek(date: Date, firstDayOfTheWeek: number = 1) {
		const startOfWeek = this.getStartOfWeek(date, firstDayOfTheWeek);
		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6);
		return endOfWeek;
	}

	public static getStartOfMonth(date: Date, firstDayOfTheWeek: number = 1): Date {
		const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
		startOfMonth.setHours(0, 0, 0, 0);
		// Get the start of the week that contains the first day of the month
		return this.getStartOfWeek(startOfMonth, firstDayOfTheWeek);
	}

	public static getEndOfMonth(date: Date, firstDayOfTheWeek: number = 1): Date {
		// Get the last day of the month
		const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
		endOfMonth.setHours(23, 59, 59, 999);
		// Get the end of the week that contains the last day of the month
		return this.getEndOfWeek(endOfMonth, firstDayOfTheWeek);
	}

	public static getCurrentWeek(datePipe: DatePipe, date: Date, us: boolean, firstDayOfTheWeek: number = 1): string {
		const startOfWeek = this.getStartOfWeek(date, firstDayOfTheWeek);
		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(endOfWeek.getDate() + 6);
		const dateFormat = us ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
		return datePipe.transform(startOfWeek, dateFormat) + ' - ' + datePipe.transform(endOfWeek, dateFormat);
	}

	public static daysOfWeek(startDate: Date, firstDayOfTheWeek: number = 1): Date[] {
		const startOfWeek = this.getStartOfWeek(startDate, firstDayOfTheWeek);

		const week = [];
		for (let i = 0; i < 7; i++) {
			const day = new Date(startOfWeek);
			day.setDate(startOfWeek.getDate() + i);
			week.push(day);
		}
		return week;
	}

	public static shiftDuration(shift: Shift): string {
		const start = new Date(shift.startTime);
		const end = new Date(shift.endTime);
		const breakMillis = shift.breakMinutes * 60 * 1000;
		const durationMillis = end.getTime() - start.getTime() - breakMillis;
		const durationHours = durationMillis / (1000 * 60 * 60);
		return durationHours.toFixed(1); // One decimal place
	}

	public static isEmployeeAvailableForShift(shiftStartTime: number, shiftEndTime: number, availableFrom: number, availableTo: number) {
		//handle overnight shifts
		if (shiftEndTime <= shiftStartTime) {
			shiftEndTime += 1440; // add 24 hours in minutes
		}

		//handle overnight availability
		if (availableTo <= availableFrom) {
			availableTo += 1440; // add 24 hours in minutes
		}

		const isAvailable = availableFrom <= shiftStartTime && availableTo >= shiftEndTime;

		return {
			status: isAvailable,
			message: isAvailable ? 'Employee Available' : `Employee is only available from ${DateHelper.serverTimeToDisplayTime(availableFrom)} and until ${DateHelper.serverTimeToDisplayTime(availableTo)} on this day`
		}
	}

	public static formatSiteAddress(project: ProjectWithShifts): string {
		if (!project?.address) {
			return "Address Not Available";
		}

		const { addressLine1, addressLine2, addressLine3, city, state, postCode } = project.address;

		const address = [
			addressLine1,
			addressLine2,
			addressLine3,
			city,
			state?.name,
			postCode,
		];

		return address.filter(Boolean).join(', ');
	}

	public static getTotalWeeklyDuration(shifts: Shift[]): number {
		let totalDuration = 0;
		shifts.forEach(shift => {
			const start = new Date(shift.startTime);
			const end = new Date(shift.endTime);
			const breakMillis = shift.breakMinutes * 60 * 1000;
			const durationMillis = end.getTime() - start.getTime() - breakMillis;
			totalDuration += durationMillis / (1000 * 60 * 60); // Convert to hours
		});
		return totalDuration;
	}
}